/**
 * Central environment configuration.
 *
 * Read config from here rather than touching `process.env` in components, so
 * that defaults and validation live in one place.
 */

const ENVIRONMENTS = ['development', 'staging', 'production'];

const rawEnv = process.env.NEXT_PUBLIC_ENV || 'development';

export const environment = ENVIRONMENTS.includes(rawEnv) ? rawEnv : 'development';

export const isDevelopment = environment === 'development';
export const isStaging = environment === 'staging';
export const isProduction = environment === 'production';

/** Base URL of inverse-api. Server-side only — the browser goes through the proxy. */
export const apiBaseUrl =
  process.env.API_BASE_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8000';

/** Public Business API prefix. The dashboard never calls /api/v1 or /api/v2 from the browser. */
export const apiPrefix = process.env.NEXT_PUBLIC_API_PREFIX || '/api/b/v1';

/**
 * Consumer auth prefix. Sign-up/sign-in run against the consumer surface
 * (`/api/v1/auth/*`) because a business session begins with a person: the
 * caller authenticates as themselves first, then picks (or creates) a
 * business to act for. Server-side only, like every API call here.
 */
export const authApiPrefix = process.env.AUTH_API_PREFIX || '/api/v1';

/**
 * The payer surface. Mounted on its own prefix rather than under /api/b/v1
 * because a payment link is a bearer token handed to somebody who has no
 * account with us — requiring one would defeat the feature. It exposes exactly
 * two things: resolving a link, and starting a payment.
 *
 * Deliberately NOT reachable through `/api/proxy`, which promotes the session
 * cookie to a Bearer header. A payer has no session, and forwarding a stray
 * cookie from someone who happens to be signed in would attach a business
 * identity to a public payment.
 */
export const publicApiPrefix = process.env.PUBLIC_API_PREFIX || '/api/public/v1';

/** Where the browser sends requests — our own Next route handler, which proxies on. */
export const clientApiPath = '/api/proxy';

export const sentryDsn = process.env.NEXT_PUBLIC_SENTRY_DSN || '';
export const sentryEnabled = Boolean(sentryDsn);

export const config = {
  environment,
  isDevelopment,
  isStaging,
  isProduction,
  apiBaseUrl,
  apiPrefix,
  authApiPrefix,
  publicApiPrefix,
  clientApiPath,
  sentryDsn,
  sentryEnabled,
};

export default config;
